import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { opacity2hex, sortArray } from "utils/miscUtils";
import { formatUtcDateTime } from "utils/contentSchedule";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo } from "utils/constants";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { getCardTypeTableColumns } from "utils/tableHelper/extraTableHelper";
import {
  archiveNotifications,
  bulkDeleteNotifications,
  bulkUpdateNotificationStatus,
  copyNotification,
  deleteNotification,
  getNotifications,
  unarchiveNotifications,
  updateNotificationStatus,
} from "services/notificationService";
import ArchiveActionBar from "components/archive/ArchiveActionBar";
import ArchiveViewButton from "components/archive/ArchiveViewButton";
import useContentArchive from "components/archive/useContentArchive";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import useConsent from "hooks/useConsent";
import ViewImageModal from "screens/modals/ViewImageModal";
import useOpenClose from "hooks/useOpenClose";

const useStyles = makeStyles(((theme: any) => ({
  editButton: {
    backgroundColor: theme.palette.common.offWhite + opacity2hex("0.80"),
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.common.offWhite,
    },
  },
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
  pickerContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "end",
    justifyContent: "space-between",
  },
  buttonContainer: {
    minWidth: 90,
    maxWidth: 120,
    display: "flex",
  },
  button: {
    height: 30,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    textTransform: "none",
    width: "100% !important",
    [theme.breakpoints.down("sm")]: {
      fontSize: 12,
      maxWidth: "100% !important",
    },
    "&:hover": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
})) as any);

const RowActions = ({ rowId, data, actionHandlers }: any) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const archived = actionHandlers?.archived;

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleEditClick = (event: any, data: any) => {
    handleClose(event);
    navigate(configData.routes.notifications.edit?.replace(":id", data?._id));
  };

  const handleDisableClick = (event: any, id: any) => {
    handleClose(event);
    actionHandlers?.handleDisable(id);
  };

  const handleCopyClick = (event: any, id: any) => {
    handleClose(event);
    actionHandlers?.handleCopyNotification(id);
  };

  const handleDeleteClick = (event: any, rowData: any) => {
    handleClose(event);
    actionHandlers?.handleDelete(rowData);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.NOTIFICATIONS.DELETE_ROLES,
  );

  const handleArchiveClick = (event: any, id: any) => {
    handleClose(event);
    actionHandlers?.handleArchive([id]);
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {archived ? (
          <MenuItem onClick={(e) => handleArchiveClick(e, data?._id)}>
            Restore
          </MenuItem>
        ) : (
          [
            <MenuItem key="edit" onClick={(e) => handleEditClick(e, data)}>
              Edit
            </MenuItem>,
            <MenuItem
              key="status"
              onClick={(e) => handleDisableClick(e, data?._id)}
            >
              {data?.isActive ? "Disable" : "Enable"}
            </MenuItem>,
            <MenuItem key="copy" onClick={(e) => handleCopyClick(e, data?._id)}>
              Copy
            </MenuItem>,
            <MenuItem
              key="archive"
              disabled={data?.isActive}
              onClick={(e) => handleArchiveClick(e, data?._id)}
            >
              Archive
            </MenuItem>,
            hasDeletePermission ? (
              <MenuItem
                key="delete"
                disabled={data?.isActive}
                onClick={(e) => handleDeleteClick(e, data)}
              >
                Delete
              </MenuItem>
            ) : null,
          ]
        )}
      </Menu>
    </>
  );
};

const Notifications = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [listData, setListData] = useState([]);
  const [orderDirection, setOrderDirection] = useState("asc");
  const { searchValue, onSearch: _onSearch } = useSearchHook();
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.NOTIFICATIONS.UPDATE_ROLES,
  );
  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.NOTIFICATIONS.DELETE_ROLES,
  );

  const { isOpen, close, open, extraData, updateExtra } = useOpenClose();

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: listData?.length });

  const archive = useContentArchive({
    label: { singular: "notification", plural: "notifications" },
    isLive: (row: any) => !!row?.isActive,
    archive: archiveNotifications,
    unarchive: unarchiveNotifications,
    bulkStatus: bulkUpdateNotificationStatus,
    bulkDelete: hasDeletePermission ? bulkDeleteNotifications : undefined,
    rows: listData,
    onCompleted: () => fetchData(searchValue),
    onViewChange: () => onChangePage(defaultPageNo),
  });

  const onColumnClick = (column: any) => {
    let _list = sortArray([...listData], orderDirection, column);
    setListData(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const createLeague = () => navigate(configData.routes.notifications.create);
  const navigateToBulkUpload = () =>
    navigate(configData.routes.notifications.bulkUploadNotifications);

  // Archived records are a read-only view, so their rows do not link into the editor.
  const rowHref = (data: any) =>
    hasEditPermission && !archive.archived
      ? configData.routes.notifications.edit?.replace(":id", data?._id)
      : null;

  const fetchData = async (_search?: any) => {
    setLoading(true);
    try {
      let payload: any = {
        pageNo,
        pageSize,
        sortDir: orderDirection,
        archived: archive.archived,
      };
      if (searchValue) payload["searchValue"] = searchValue;
      const resp = await getNotifications(payload);
      setListData(
        resp?.data?.items?.map((item) => ({
          ...item,
          isActiveText: item.isActive ? "YES" : "NO",
          rowDisabled: !item?.isActive,
          copiedFrom: item?.copiedFrom || "-",
          startDateText: formatUtcDateTime(item?.startDate),
          endDateText: formatUtcDateTime(item?.endDate),
        })),
      );
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message);
    }
  };

  const onSearch = (value: any) => {
    // reset filters if any
    _onSearch(value);
    onChangePage(defaultPageNo);
  };

  const onDisable = async (id) => {
    try {
      const resp = await updateNotificationStatus(id);
      showSnackbar(resp?.data?.message);
      fetchData(searchValue);
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const onDelete = async (id) => {
    try {
      const resp = await deleteNotification(id);
      showSnackbar(resp?.message);
      fetchData(searchValue);
      closeConsent();
    } catch (error) {
      closeConsent();
      showSnackbar(error.message, "error");
    }
  };

  const onCopy = async (id: any) => {
    try {
      setLoading(true);
      const resp = await copyNotification(id);
      await fetchData();
      showSnackbar(resp?.message);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  const _openConsentDialog = (data: any) => {
    openConsent();
    updateDetails({ itemName: data?.title, id: data?._id });
  };

  const openModel = (data: any) => {
    updateExtra({
      imageUrl: data.imageUrl,
    });
    open();
  };

  useEffect(() => {
    const initialColumns = getCardTypeTableColumns().filter(
      (col) => col.id !== "actions" || hasEditPermission,
    );
    setColumns(initialColumns);

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchValue, archive.archived]);

  return (
    <ContentShell
      title={archive.archived ? "Archived Notifications" : "Notifications"}
      loading={loading}
      actions={
        <Box style={{ display: "flex", flexFlow: "row nowrap", gap: 4 }}>
          <SearchInput
            value={searchValue}
            placeholder="Search a notification"
            onSearch={onSearch}
          />
          <ArchiveViewButton archive={archive} />
          {!archive.archived &&
            sessionHelper.hasRole(PermittedRoles.NOTIFICATIONS.CREATE_ROLES) && (
              <CustomButton onClick={navigateToBulkUpload}>
                Bulk Upload
              </CustomButton>
            )}
          {!archive.archived &&
            sessionHelper.hasRole(PermittedRoles.LEAGUE.CREATE_ROLES) && (
              <CustomButton onClick={createLeague}>Create</CustomButton>
            )}
        </Box>
      }
    >
      <Box className={styles.container}>
        <ArchiveActionBar archive={archive} rows={listData} nameKey="title" />
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={listData.length}
          highlightAltRow={true}
          actions={RowActions}
          onChangePage={onChangePage}
          onColumnClick={onColumnClick}
          onRowsPerPageChange={onPageSizeChange}
          rowHref={rowHref}
          selectable={hasEditPermission}
          selectedIds={archive.selectedIds}
          onSelectionChange={archive.setSelectedIds}
          rowSelectDisabledReason={archive.rowSelectDisabledReason}
          selectAllOptions={archive.selectAllOptions}
          actionHandlers={{
            openModal: openModel,
            handleDisable: onDisable,
            handleDelete: _openConsentDialog,
            handleCopyNotification: onCopy,
            handleArchive: archive.requestAction,
            archived: archive.archived,
          }}
        />
        <ViewImageModal
          label="Notification Image"
          imageSource={extraData.imageUrl}
          isOpen={isOpen}
          close={close}
          customStyle={{ width: "60%" }}
        />
        <DeleteConsentDialog
          itemName={constentDetails.itemName}
          open={isOpenConsent}
          onClose={closeConsent}
          onConfirm={() => onDelete(constentDetails.id)}
        />
      </Box>
    </ContentShell>
  );
};

export default Notifications;
